import debounce from "lodash.debounce";
import { track } from "~/modules/analytics/track";
import { AssetScreenerUserEvents } from "~/modules/analytics/events/asset-screener";

// // // //

/**
 * trackFilterSearch
 * Tracks the event when a user searches for assets.
 * NOTE - the search input fires on every keystroke, so we debounce this to
 * avoid spamming our analytics backend with partial-query events.
 */
const trackFilterSearchDebounced = debounce((query: string) => {
    track(AssetScreenerUserEvents.FilterSearch, {
        query,
    });
}, 1000);

export function trackFilterSearch(query: string) {
    trackFilterSearchDebounced(query);
}

/**
 * trackFilterYieldRange
 * Tracks the event when a user changes the yield range filter
 */
export function trackFilterYieldRange(range: [number, number]) {
    track(AssetScreenerUserEvents.FilterYieldRange, {
        min: range[0],
        max: range[1],
    });
}

/**
 * trackFilterMinAUM
 * Tracks the event when a user changes the AUM range filter
 */
// export function trackFilterMinAUM(range: [number, number]) {
//     track(AssetScreenerUserEvents.FilterMinAUM, {
//         min: range[0],
//         max: range[1],
//     });
// }

/**
 * trackFilterRedemption
 * Tracks the event when a user changes the redemption filter
 */
export function trackFilterRedemption(selected: string[]) {
    track(AssetScreenerUserEvents.FilterRedemption, {
        selected,
    });
}

/**
 * trackFilterNetwork
 * Tracks the event when a user changes the network filter
 */
export function trackFilterNetwork(selected: string[]) {
    track(AssetScreenerUserEvents.FilterNetwork, {
        selected,
    });
}

/**
 * trackFilterNonKyc
 * Tracks the event when a user toggles the Non-KYC filter
 */
export function trackFilterNonKyc(enabled: boolean) {
    track(AssetScreenerUserEvents.FilterNonKyc, {
        enabled,
    });
}

/**
 * trackFilterAssetClass
 * Tracks the event when a user changes the asset class filter
 */
export function trackFilterAssetClass(selected: string[]) {
    track(AssetScreenerUserEvents.FilterAssetClass, {
        selected,
    });
}

/**
 * trackFilterSortBy
 * Tracks the event when a user changes the sort by filter
 */
export function trackFilterSortBy(sortBy: string) {
    track(AssetScreenerUserEvents.FilterSortBy, {
        sortBy,
    });
}

/**
 * trackFilterClearAll
 * Tracks the event when a user clears all filters
 */
export function trackFilterClearAll(activeFilterCount: number) {
    track(AssetScreenerUserEvents.FilterClearAll, {
        activeFilterCount,
    });
}

/**
 * trackAssetRowExpand
 * Tracks the event when a user expands an asset row to view its details.
 */
export function trackAssetRowExpand(props: {
    asset_id: number;
    asset_ticker: string;
    toggle_source: "chevron_button" | "data_room_badge";
}) {
    track(AssetScreenerUserEvents.AssetRowExpand, {
        asset_id: String(props.asset_id),
        asset_ticker: props.asset_ticker,
        source: props.toggle_source,
    });
}

/**
 * trackAssetRowCollapse
 * Tracks the event when a user collapses an expanded asset row.
 */
export function trackAssetRowCollapse(props: {
    asset_id: number;
    asset_ticker: string;
    toggle_source: "chevron_button" | "data_room_badge";
}) {
    track(AssetScreenerUserEvents.AssetRowCollapse, {
        asset_id: String(props.asset_id),
        asset_ticker: props.asset_ticker,
        toggle_source: props.toggle_source,
    });
}

/**
 * trackPerformanceChartChangeTab
 * Tracks the event when a user switches the performance chart view inside an
 * expanded asset row.
 */
export function trackPerformanceChartChangeTab(props: {
    asset_id: number;
    asset_name: string;
    prev_value: string;
    next_value: string;
}) {
    track(AssetScreenerUserEvents.PerformanceChartChangeTab, {
        asset_id: String(props.asset_id),
        asset_name: props.asset_name,
        prev_value: props.prev_value,
        next_value: props.next_value,
    });
}

/** Which document surface the data-room access flow is opening. */
export type DataRoomAccessType = "data-room" | "factsheet";

/**
 * trackAssetCardSubmitDataRoomAccess
 * Tracks the event when a user submits the data-room access form to open a
 * document.
 */
/**
 * trackAssetCardClickBookMeeting
 * Tracks the event when a user opens the cal.com booking modal from an asset
 * card's "Book Meeting" CTA.
 */
export function trackAssetCardClickBookMeeting(props: {
    asset_id: number;
    asset_ticker: string;
    manager_name: string;
    cal_link: string;
}) {
    track(AssetScreenerUserEvents.AssetCardClickBookMeeting, {
        asset_id: String(props.asset_id),
        asset_ticker: props.asset_ticker,
        manager_name: props.manager_name,
        cal_link: props.cal_link,
    });
}

export function trackAssetCardSubmitDataRoomAccess(props: {
    asset_id: number;
    asset_ticker: string;
    document_name: string;
    access_type: DataRoomAccessType;
}) {
    track(AssetScreenerUserEvents.AssetCardSubmitDataRoomAccess, {
        asset_id: String(props.asset_id),
        asset_ticker: props.asset_ticker,
        document_name: props.document_name,
        access_type: props.access_type,
    });
}
